'use client';

import { useState } from 'react';
import Link from 'next/link';
import { authApi } from '@/lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        'שליחת קישור איפוס נכשלה. אנא נסה שוב.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-primary-900 text-white py-4">
          <div className="container mx-auto px-4">
            <Link href="/" className="text-2xl font-bold">
              AmAm
            </Link>
          </div>
        </header>

        {/* Success Message */}
        <div className="container mx-auto px-4 py-12 max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              קישור איפוס נשלח!
            </h1>
            <p className="text-gray-600 mb-6">
              אם כתובת האימייל קיימת במערכת, נשלח אליה קישור לאיפוס הסיסמה.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-right mb-6">
              <p className="text-sm text-blue-800">
                <strong>שים לב:</strong> קישור האיפוס תקף לשעה אחת בלבד.
              </p>
              <p className="text-sm text-blue-700 mt-2">
                לא קיבלת אימייל? בדוק את תיקיית הספאם.
              </p>
            </div>
            <Link
              href="/login"
              className="inline-block bg-primary-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-800 transition"
            >
              חזור להתחברות
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary-900 text-white py-4">
        <div className="container mx-auto px-4">
          <Link href="/" className="text-2xl font-bold">
            AmAm
          </Link>
        </div>
      </header>

      {/* Forgot Password Form */}
      <div className="container mx-auto px-4 py-12 max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-center mb-2">שכחת סיסמה?</h1>
          <p className="text-gray-600 text-center mb-6">
            הכנס את כתובת האימייל שלך ונשלח לך קישור לאיפוס הסיסמה.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                אימייל *
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                placeholder="your@email.com"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? 'שולח...' : 'שלח קישור איפוס'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              נזכרת בסיסמה?{' '}
              <Link href="/login" className="text-primary-900 font-medium hover:underline">
                התחבר כאן
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link href="/register" className="text-sm text-gray-500 hover:text-gray-700">
              אין לך חשבון? הירשם כאן
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
